import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import axios from 'axios';
import Cookies from 'js-cookie';
import { DiffChecker_URL } from '../../Components/Constant';
import MyTableComponent from './DiffCheckerNewDataRender';
import stringSimilarity from 'string-similarity';
import {
  Select,
  Button,
  Spin,
  Upload,
  Typography,
  message,
  Space
} from 'antd';
import { UploadOutlined ,UserOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;

export default function CSVUploader() {
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [fileData, setFileData] = useState([]);
  const [fileInfo, setFileInfo] = useState('Click to select CSV file');
  const [accounts, setAccounts] = useState([]);
  const [destinationSelection, setDestinationSelection] = useState(null);
  const [objectList, setObjectList] = useState([]);
  const [objectSelection, setObjectSelection] = useState(null);
  const [externalIdFields, setExternalIdFields] = useState([]);
  const [externalIdFieldSelection, setExternalIdFieldSelection] = useState(null);
  const [fields, setFields] = useState([]);
  const [responseData, setResponseData] = useState(null);
  const [loadingDestination, setLoadingDestination] = useState(false);
  const [loadingObjects, setLoadingObjects] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // Fetch accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      const token = Cookies.get('tokenRes');
      try {
        const response = await axios.get(DiffChecker_URL + '/query/allUsers', {
          headers: { JWTToken: token }
        });
        setAccounts(response.data);
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    };
    fetchAccounts();
  }, []);

  const handleFileChange = (file) => {
    setFile(file);
    setFileInfo(file.name);
    parseCSV(file);
    return false; // prevent auto upload
  };

  const parseCSV = (file) => {
    Papa.parse(file, {
      complete: (result) => {
        const headers = result.data[0].map((columnName) => ({
          columnName,
          value: '',
          destinationSelection: []
        }));
        const rows = [];
        for (let i = 1; i < result.data.length; i++) {
          const rowData = {};
          headers.forEach((h, j) => {
            rowData[h.columnName] = result.data[i][j] || null;
          });
          rows.push(rowData);
        }
        setHeaders(headers);
        setFileData(rows);
      },
      header: false,
      skipEmptyLines: true
    });
  };

  const clearData = () => {
    setFile(null);
    setHeaders([]);
    setFileInfo('Click to select CSV file');
  };

  const handleDestinationChange = async (userId) => {
    setDestinationSelection([{ id: userId }]);
    setLoadingDestination(true);
    try {
      const response = await axios.post(DiffChecker_URL + '/query/Objects', {
        UserId: userId
      });
      setObjectList(response.data);
    } catch (error) {
      console.error('Error fetching objects:', error);
    } finally {
      setLoadingDestination(false);
    }
  };

  const handleObjectChange = async (objectName) => {
    setObjectSelection([{ label: objectName }]);
    setLoadingObjects(true);
    try {
      const response = await axios.post(`${DiffChecker_URL}/query/fields`, {
        UserId: destinationSelection[0].id,
        ObjectName: objectName
      });
      setExternalIdFields(response.data.externalIdFields);
      setFields(response.data.fields);
    } catch (error) {
      console.error('Error fetching fields:', error);
    } finally {
      setLoadingObjects(false);
    }
  };

  const sendData = async () => {
    setLoadingSubmit(true);
    try {
      const formattedData = {
        fileData,
        mappings: {},
        destinationId: destinationSelection[0]?.id || '',
        objectName: objectSelection[0]?.label || '',
        externalIdField: externalIdFieldSelection[0]?.label || ''
      };

      headers.forEach((h) => {
        if (h.destinationSelection.length > 0) {
          formattedData.mappings[h.columnName] = h.destinationSelection[0].label;
        }
      });

      const response = await axios.post(
        `${DiffChecker_URL}/map-csv-to-salesforce`,
        formattedData
      );
      setResponseData(response.data);
    } catch (error) {
      console.error('Error sending data:', error);
      message.error('Error while sending data');
    } finally {
      setLoadingSubmit(false);
    }
  };

  const fuzzyMatchFields = () => {
    const updated = headers.map((header) => {
      let bestMatch = null;
      let bestScore = 0;

      fields.forEach((field) => {
        const headerName = header.columnName.replace(/\s/g, '').toLowerCase();
        const fieldName = field.replace(/\s/g, '').toLowerCase();

        if (headerName === fieldName) {
          bestMatch = field;
          bestScore = 1;
        } else {
          const score = stringSimilarity.compareTwoStrings(headerName, fieldName);
          if (score > bestScore) {
            bestMatch = field;
            bestScore = score;
          }
        }
      });

      if (bestMatch) {
        return {
          ...header,
          destinationSelection: [{ label: bestMatch, id: bestMatch }]
        };
      }
      return header;
    });

    setHeaders(updated);
  };

  return (
    <div style={{ width: '80%', margin: 'auto', paddingBottom: '20px' }}>
      <Title level={3} style={{ textAlign: 'center', marginBottom: '20px' }}>
        CSV Loader
      </Title>

      <Spin spinning={loadingDestination || loadingObjects || loadingSubmit}>
        {!responseData && (
          <div style={{ display: 'flex', gap: '20px' }}>
            {/* Left Side */}
            <div style={{ flex: 1 }}>
              {/* Upload */}
              <Upload
                beforeUpload={handleFileChange}
                onRemove={clearData}
                showUploadList={false}
                accept=".csv"
              >
                <Button icon={<UploadOutlined />}>Upload File</Button>
                <span style={{ marginLeft: '10px' }}>{fileInfo}</span>
              </Upload>

              {/* Destination */}
              <div style={{ marginTop: '15px' }}>
                <Select
                  placeholder="Select Destination"
                  style={{ width: '100%' }}
                  value={destinationSelection?.label}
                  onChange={handleDestinationChange}
                >
                 {accounts.map((account) => (
                            <Option key={account.Id} value={account.Id}>
                                <Space>
                                    <UserOutlined />
                                    {account.Name}
                                </Space>
                            </Option>
                        ))}
                </Select>
              </div>

              {/* Object */}
              <div style={{ marginTop: '15px' }}>
                <Select
                  showSearch
                  allowClear
                  placeholder="Select Object"
                  style={{ width: '100%' }}
                  value={objectSelection}
                  onChange={handleObjectChange}
                >
                  {objectList.map((o) => (
                    <Option key={o} value={o}>
                      {o}
                    </Option>
                  ))}
                </Select>
              </div>

              {/* External Id */}
              <div style={{ marginTop: '15px' }}>
                <Select
                allowClear
                  placeholder="Select External Id"
                  style={{ width: '100%' }}
                  value={externalIdFieldSelection}
                  onChange={(val) =>
                    setExternalIdFieldSelection([{ label: val, id: val }])
                  }
                >
                  {externalIdFields?.map((f) => (
                    <Option key={f} value={f}>
                      {f}
                    </Option>
                  ))}
                </Select>
              </div>

              <Button
                type="primary"
                style={{ marginTop: '20px' }}
                onClick={sendData}
              >
                Submit
              </Button>
            </div>

            {/* Right Side */}
            <div style={{ flex: 1 }}>
              {file && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Title level={4} style={{ margin: 0 }}>
                      Map fields with your column
                    </Title>
                    <Button
                      style={{ marginLeft: '10px' }}
                      onClick={fuzzyMatchFields}
                    >
                      Fuzzy Match
                    </Button>
                  </div>

                  {headers.map((header, index) => (
                    <div key={index} style={{ marginTop: '10px' }}>
                      <span>{header.columnName}</span>
                      <Select
                        showSearch
                        allowClear
                        placeholder="Search Field"
                        style={{ width: '100%', marginTop: '5px' }}
                        value={header.destinationSelection[0]?.label}
                        onChange={(val) => {
                          const newHeaders = [...headers];
                          newHeaders[index].destinationSelection = [{ label: val, id: val }];
                          setHeaders(newHeaders);
                        }}
                      >
                        {fields.map((f) => (
                          <Option key={f} value={f}>
                            {f}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {responseData && (
          <div style={{ marginTop: '20px' }}>
            <Button onClick={() => setResponseData(null)}>Edit</Button>
            <div style={{ marginTop: '10px' }}>
              <MyTableComponent
                data={responseData}
                sourcename="Source CSV"
                destinationname={destinationSelection[0]?.label}
              />
            </div>
          </div>
        )}
      </Spin>
    </div>
  );
}
